import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { ScrollArea } from '../../ui/scroll-area';
import { Checkbox } from '../../ui/checkbox';
import { Loader2, Search, User } from 'lucide-react';
import { useApiBase } from '../../../hooks/useApiBase';
import { useAuth } from '../../../context/AuthContext';
import { listUsers, type UserRecord } from '../../../api/users';
import { useMessagesContext } from '../../../context/MessagesContext';
import { handleApiError } from '../../../utils/errorUtils';
import { cn } from '../../../lib/utils';

interface NewConversationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function NewConversationDialog({ open, onOpenChange }: NewConversationDialogProps) {
  const apiBase = useApiBase();
  const { user, isAdmin } = useAuth();
  const { sendMessage, fetchConversations } = useMessagesContext();
  
  const [search, setSearch] = useState('');
  const [users, setUsers] = useState<UserRecord[]>([]);
  const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(new Set());
  const [messageText, setMessageText] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchUsersList = useCallback(async (searchTerm?: string) => {
    // Skip API call for non-admin users (endpoint requires admin access)
    if (!isAdmin) {
      setUsers([]);
      setLoading(false);
      setError('User selection requires admin access');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const userList = await listUsers(apiBase, searchTerm);
      // Filter out current user
      const filteredUsers = userList.filter((u) => u.id !== user?.id);
      setUsers(filteredUsers);
    } catch (err) {
      setError(handleApiError(err, 'load users'));
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [apiBase, user?.id, isAdmin]);

  useEffect(() => {
    if (open) {
      fetchUsersList();
      setSearch('');
      setSelectedUserIds(new Set());
      setMessageText('');
      setError(null);
    }
  }, [open, fetchUsersList]);

  const handleSearch = useCallback(
    async (searchTerm: string) => {
      setSearch(searchTerm);
      await fetchUsersList(searchTerm);
    },
    [fetchUsersList]
  );

  const toggleUser = useCallback((userId: string) => {
    setSelectedUserIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }
      return next;
    });
  }, []);

  const handleSend = useCallback(async () => {
    if (selectedUserIds.size === 0) {
      setError('Please select at least one user');
      return;
    }
    if (!messageText.trim()) {
      setError('Please enter a message');
      return;
    }

    setSending(true);
    setError(null);
    try {
      const recipients = Array.from(selectedUserIds);
      await sendMessage(recipients, messageText.trim());
      await fetchConversations();
      onOpenChange(false);
      // Reset form
      setSelectedUserIds(new Set());
      setMessageText('');
      setSearch('');
    } catch (err) {
      setError(handleApiError(err, 'send message'));
    } finally {
      setSending(false);
    }
  }, [selectedUserIds, messageText, sendMessage, fetchConversations, onOpenChange]);

  const handleClose = useCallback(() => {
    if (!sending) {
      setSelectedUserIds(new Set());
      setMessageText('');
      setSearch('');
      setError(null);
      onOpenChange(false);
    }
  }, [sending, onOpenChange]);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>New Conversation</DialogTitle>
          <DialogDescription>
            Select users to message and send your first message.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div>
            <Label htmlFor="user-search">Search Users</Label>
            <div className="relative mt-2">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                id="user-search"
                placeholder="Search by name or email..."
                value={search}
                onChange={(e) => handleSearch(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>
          <div>
            <Label>Recipients ({selectedUserIds.size})</Label>
            <ScrollArea className="h-32 border rounded-md mt-2">
              {loading ? (
                <div className="flex items-center justify-center p-4">
                  <Loader2 className="h-4 w-4 animate-spin text-primary" />
                </div>
              ) : users.length === 0 ? (
                <div className="p-4 text-sm text-muted-foreground text-center">
                  {search ? 'No users found' : 'Start typing to search for users'}
                </div>
              ) : (
                <div className="p-2 space-y-2">
                  {users.map((user) => {
                    const isSelected = selectedUserIds.has(user.id);
                    const displayName =
                      user.display_name ||
                      [user.first_name, user.last_name].filter(Boolean).join(' ') ||
                      user.email ||
                      user.username ||
                      'Unknown User';
                    return (
                      <div
                        key={user.id}
                        className={cn(
                          'flex items-center gap-3 p-2 rounded-md hover:bg-muted/50 cursor-pointer',
                          isSelected && 'bg-primary/10'
                        )}
                        onClick={() => toggleUser(user.id)}
                      >
                        <Checkbox checked={isSelected} onCheckedChange={() => toggleUser(user.id)} />
                        <User className="h-4 w-4 text-muted-foreground" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{displayName}</p>
                          {user.email && user.email !== displayName && (
                            <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </ScrollArea>
          </div>
          <div>
            <Label htmlFor="message">Message</Label>
            <Textarea
              id="message"
              placeholder="Type your message..."
              value={messageText}
              onChange={(e) => setMessageText(e.target.value)}
              rows={4}
              className="mt-2"
            />
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={sending}>
            Cancel
          </Button>
          <Button onClick={handleSend} disabled={selectedUserIds.size === 0 || !messageText.trim() || sending}>
            {sending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Sending...
              </>
            ) : (
              'Send Message'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

